import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { PERMISSIONS, PERMISSION_MATRIX, type Permission } from '@lensmira/domain'
import { formatDayTime } from '@lensmira/i18n'

import type { SupportAccessView } from '../api/reads.js'
import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface } from './DataSurface.js'

/**
 * `ADM-005`, `ROLE-005`: the shop opens, and closes, support's door to its own records.
 *
 * **The state comes first and is stated in words.** Open or closed is the one thing an owner opens
 * this panel to learn, so it is the first line and the only thing styled as a status. A closed
 * door reads "Support cannot see anything", not an empty list.
 *
 * **Nothing is pre-ticked and the time has no default.** `ADM-005` names neither a scope nor a
 * window, so the form cannot start from one: choosing is the consent. The send button stays off
 * until both are stated, and the ceiling comes from the server's own figure, so the form refuses
 * what the gate would, before the write queue sees it. A refused queued write halts every write
 * behind it, which is why the server's refusals are the last line here and not the first.
 *
 * **Withdrawing asks for nothing but the tap.** It only ever takes access away, so it has no reason
 * field and no confirmation step to get in the way of an owner who wants support out now.
 *
 * Both writes go through the write queue (`OFF-001a`). The grant's idempotency key is minted at
 * enqueue, so a retry is the same grant.
 */

export interface SupportAccessProps {
  readonly timezone: string
  readonly load: () => Promise<SupportAccessView>
}

const MINUTES_PER_HOUR = 60

export function SupportAccess({ timezone, load }: SupportAccessProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [view, setView] = useState<SupportAccessView | null>(null)
  const [failed, setFailed] = useState(false)
  const [scope, setScope] = useState<readonly Permission[]>([])
  const [minutes, setMinutes] = useState('')
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    void load()
      .then((loaded) => {
        setView(loaded)
        setFailed(false)
      })
      .catch(() => {
        setFailed(true)
      })
  }, [load])

  useEffect(() => {
    refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setScope([])
    setMinutes('')
    refresh()
  })

  const live = view?.consents.find((consent) => consent.state === 'live')
  const max = view?.maxDurationMinutes ?? 0
  const parsed = /^\d+$/.test(minutes.trim()) ? Number(minutes.trim()) : null
  const durationProblem =
    minutes.trim() === ''
      ? null
      : parsed === null || parsed < 1
        ? t('supportAccess.durationInvalid')
        : parsed > max
          ? t('supportAccess.durationTooLong', { count: max })
          : null
  const ready = max > 0 && scope.length > 0 && parsed !== null && durationProblem === null

  const toggle = useCallback((permission: Permission) => {
    setScope((previous) =>
      previous.includes(permission)
        ? previous.filter((p) => p !== permission)
        : [...previous, permission],
    )
  }, [])

  const grant = useCallback(async () => {
    if (!ready || parsed === null) return
    const write = (await enqueue({
      method: 'POST',
      path: '/support-access',
      label: t('supportAccess.queueGrant'),
      body: { scope: [...scope], durationMinutes: parsed },
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, parsed, ready, scope, t])

  const withdraw = useCallback(
    async (consentId: string) => {
      const write = (await enqueue({
        method: 'POST',
        path: `/support-access/${encodeURIComponent(consentId)}/revoke`,
        label: t('supportAccess.queueWithdraw'),
        body: {},
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const rows = useMemo(
    () =>
      PERMISSIONS.map((id) => ({
        id,
        label: PERMISSION_MATRIX[id].label,
        sensitive: PERMISSION_MATRIX[id].sensitive,
      })),
    [],
  )
  const labelOf = (id: string): string =>
    (PERMISSION_MATRIX as Record<string, { label: string } | undefined>)[id]?.label ?? id

  const line = statusLine(t, snapshot, writeId, t('queue.subject.setup'))
  const earlier = view?.consents.filter((consent) => consent.state !== 'live') ?? []

  return (
    <section className="lm-screen" aria-labelledby="support-access-heading">
      <h2 id="support-access-heading" className="lm-setup-panel__heading">
        {t('supportAccess.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('supportAccess.means')}</p>

      {/*
        There is no empty state: a closed door is a state of the view, not an empty list. `failed`
        also fires when the refresh after a write fails, and the error then replaces the view.
      */}
      <DataSurface
        state={
          failed
            ? { kind: 'error', message: t('supportAccess.loadFailed'), onRetry: refresh }
            : view === null
              ? { kind: 'loading' }
              : { kind: 'ready' }
        }
        shape="lines"
        rows={3}
        what={t('supportAccess.what')}
      >
        {view === null ? null : (
          <>
            <div
              className={
                live === undefined
                  ? 'lm-support-state lm-support-state--closed'
                  : 'lm-support-state lm-support-state--open'
              }
              role="status"
            >
              <p className="lm-support-state__line">
                {live === undefined
                  ? t('supportAccess.closed')
                  : t('supportAccess.open', {
                      until: formatDayTime(live.expiresAt, timezone, locale),
                    })}
              </p>
              {live === undefined ? null : (
                <>
                  <p className="lm-hint">{t('supportAccess.mayRead')}</p>
                  <ul className="lm-support-scope">
                    {live.scope.map((id) => (
                      <li key={id} className="lm-support-scope__item" dir="auto">
                        {labelOf(id)}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="lm-button lm-button--danger"
                    disabled={writeId !== null}
                    onClick={() => {
                      void withdraw(live.consentId)
                    }}
                  >
                    {t('supportAccess.withdraw')}
                  </button>
                </>
              )}
            </div>

            <div className="lm-setup-panel">
              <h3 className="lm-setup-panel__heading">
                {live === undefined
                  ? t('supportAccess.grantHeading')
                  : t('supportAccess.replaceHeading')}
              </h3>
              {live === undefined ? null : (
                <p className="lm-setup-panel__effects">{t('supportAccess.replaceMeans')}</p>
              )}
              <fieldset className="lm-field lm-support-choices">
                <legend className="lm-field__label">{t('supportAccess.scopeLegend')}</legend>
                {rows.map((row) => (
                  <label key={row.id} className="lm-support-choice">
                    <input
                      type="checkbox"
                      checked={scope.includes(row.id)}
                      onChange={() => {
                        toggle(row.id)
                      }}
                    />{' '}
                    <span dir="auto">{row.label}</span>
                    {row.sensitive ? (
                      <span className="lm-support-choice__tag">{t('supportAccess.sensitive')}</span>
                    ) : null}
                  </label>
                ))}
              </fieldset>
              <label className="lm-field">
                <span className="lm-field__label">{t('supportAccess.durationLabel')}</span>
                <input
                  className="lm-input"
                  dir="ltr"
                  inputMode="numeric"
                  autoComplete="off"
                  value={minutes}
                  aria-invalid={durationProblem !== null}
                  onChange={(e) => {
                    setMinutes(e.currentTarget.value)
                  }}
                />
              </label>
              <p className="lm-hint">
                {t('supportAccess.durationHint', { count: max, hours: max / MINUTES_PER_HOUR })}
              </p>
              {durationProblem === null ? null : <p className="lm-error">{durationProblem}</p>}
              <button
                type="button"
                className="lm-button lm-button--primary"
                disabled={!ready || writeId !== null}
                onClick={() => {
                  void grant()
                }}
              >
                {t('supportAccess.grant')}
              </button>
              {scope.length === 0 ? (
                <p className="lm-hint">{t('supportAccess.scopeNone')}</p>
              ) : null}
            </div>

            <h3 className="lm-setup-panel__heading">{t('supportAccess.activityHeading')}</h3>
            {view.uses.length === 0 ? (
              <p className="lm-hint">{t('supportAccess.activityNone')}</p>
            ) : (
              <ul className="lm-setup-panel__people">
                {view.uses.map((use, index) => (
                  <li key={`${use.at}-${String(index)}`} className="lm-setup-person">
                    <span className="lm-setup-person__name" dir="auto">
                      {use.allowed
                        ? t('supportAccess.usedBy', { name: use.operatorName })
                        : t('supportAccess.refusedTo', { name: use.operatorName })}
                    </span>
                    <span className="lm-setup-person__meta">
                      {formatDayTime(use.at, timezone, locale)}
                    </span>
                    <span className="lm-hint" dir="auto">
                      {t('supportAccess.reasonGiven', { reason: use.reason })}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {earlier.length === 0 ? null : (
              <details className="lm-support-history">
                <summary>{t('supportAccess.historyHeading')}</summary>
                <ul className="lm-setup-panel__people">
                  {earlier.map((consent) => (
                    <li key={consent.consentId} className="lm-setup-person">
                      <span className="lm-setup-person__name">
                        {consent.state === 'revoked'
                          ? t('supportAccess.withdrawnState')
                          : t('supportAccess.expiredState')}
                      </span>
                      <span className="lm-setup-person__meta">
                        {t('supportAccess.span', {
                          from: formatDayTime(consent.grantedAt, timezone, locale),
                          until: formatDayTime(consent.expiresAt, timezone, locale),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </DataSurface>
      {line === '' ? null : <p className="lm-hint">{line}</p>}
    </section>
  )
}
